import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import GameSignals from '@/signals/GameSignals'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import { ModulesFacade } from '@modules/src/ModulesFacade'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { paintViewportCoverOverlay } from '@dragonframework/modules'
import * as PIXI from 'pixi.js'
import { FillGradient } from 'pixi.js'
import gsap from 'gsap'

const FEATURE_INTRO_OVERLAY_OPACITY = 0.6
const FEATURE_INTRO_BACKGROUND_TRANSITION_MS = 2000
//## Matches the 'you_won_in' clip length in popup_message_box.json (0.6667s)
//## should only reveal once that animation has finished playing.
export const FEATURE_INTRO_IN_ANIMATION_MS = 667
let featureIntroDisplayTimeoutId: number | null = null
let featureIntroLabelReveal: gsap.core.Tween | null = null
let featureIntroLogoReveal: gsap.core.Tween | null = null
let featureIntroContinueReveal: gsap.core.Tween | null = null

const translate = (key: string, fallback: string): string => {
    const translated = TranslationUtils.translate(key)
    return translated === key ? fallback : translated
}

//## Free Spins announces itself with a text label; Hold & Win uses the hold_win_logo sprite.
const isFreeSpinsIntro = (data: ICustomEventData): boolean => {
    const info = (data.signalData ?? {}) as { kind?: 'freespins' | 'holdandwin'; retrigger?: boolean }
    return info.kind === 'freespins'
}

//## Length of the box's 'you_won_out' clip in popup_message_box.json.
export const FEATURE_POPUP_OUT_ANIMATION_S = 0.5 //0.7333

//## Fade out the component's element (if it exists) over FEATURE_POPUP_OUT_ANIMATION_S seconds, then hide it and reset its alpha to 1.
export const fadeOutFeaturePopupItem = (data: ICustomEventData): void => {
    const el = data.component?.componentElement as PIXI.Container | undefined
    if (!el) {
        return
    }

    gsap.killTweensOf(el)

    if (!el.visible) {
        el.alpha = 1
        return
    }

    gsap.to(el, {
        alpha: 0,
        duration: FEATURE_POPUP_OUT_ANIMATION_S,
        ease: 'power1.in',
        onComplete: () => {
            el.visible = false
            el.alpha = 1
        },
    })
}

export const GRADIENT_GOLD_FILL = new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    textureSpace: 'local',
    colorStops: [
        { offset: 0, color: 0xfff7c2 },
        { offset: 0.35, color: 0xfcd34a },
        { offset: 0.65, color: 0xf0a81e },
        { offset: 1, color: 0xb8720c },
    ],
})

const scheduleFeatureIntroDisplay = (data?: {
    kind: 'freespins' | 'holdandwin'
    freeSpinsCount?: number
    retrigger?: boolean
}) => {
    if (featureIntroDisplayTimeoutId !== null) {
        clearTimeout(featureIntroDisplayTimeoutId)
    }

    // Retrigger is already on the FS background — do not wait for the 2s feature crossfade.
    const delayMs = data?.retrigger ? 50 : FEATURE_INTRO_BACKGROUND_TRANSITION_MS

    featureIntroDisplayTimeoutId = window.setTimeout(() => {
        featureIntroDisplayTimeoutId = null
        ;(GameSignals as any).featureIntroDisplayRequested?.emit(data)
    }, delayMs)
}

const clearFeatureIntroDisplaySchedule = () => {
    if (featureIntroDisplayTimeoutId === null) {
        return
    }

    clearTimeout(featureIntroDisplayTimeoutId)
    featureIntroDisplayTimeoutId = null
}

export const FEATURE_INTRO_POPUP_COMPONENT: IComponent = {
    name: ContainersNames.FEATURE_INTRO_POPUP_CONTAINER,
    type: AssetType.CONTAINER,
    zIndex: new Map<Orientation, number>([
        [Orientation.LANDSCAPE, 440],
        [Orientation.PORTRAIT, 440],
    ]),
    visible: false,
    events: [
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).featureIntroRequested],
            callback: function (data: ICustomEventData) {
                scheduleFeatureIntroDisplay(data.signalData)
            },
        },
        {
            event: ComponentEvent.SHOW,
            onGameSignalsTriggered: [(GameSignals as any).featureIntroDisplayRequested],
        },
        {
            event: ComponentEvent.HIDE,
            onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
        },
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
            callback: function () {
                clearFeatureIntroDisplaySchedule()
            },
        },
    ],
    items: [
        {
            name: 'Feature Intro Overlay',
            type: AssetType.OVERLAY,
            componentStyle: {
                color: 0x01102a,
                opacity: FEATURE_INTRO_OVERLAY_OPACITY,
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [
                        (GameSignals as any).featureIntroDisplayRequested,
                        ModuleSignals.gameResized,
                    ],
                    callback: function (data: ICustomEventData) {
                        paintViewportCoverOverlay(
                            data.component.componentElement as PIXI.Graphics,
                            0x01102a,
                            FEATURE_INTRO_OVERLAY_OPACITY
                        )
                    },
                },
            ],
        },
        {
            name: 'Feature Intro Spine',
            type: AssetType.SPINE,
            visible: false,
            src: 'popup_message_box',
            animationSettings: {
                default: {
                    triggerGameSignals: [],
                    animNames: ['you_won_in'],
                    loops: [false],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroDisplayRequested],
                    callback: function (data: ICustomEventData) {
                        const animation: SpineAnimation = data.component.componentElement
                        const parentContainer: any =
                            data.component.parentContainer ||
                            (animation as any)?.parent ||
                            data.component.componentElement

                        if (!parentContainer) {
                            ;(GameSignals as any).featureIntroContinue?.emit()
                            return
                        }

                        const state = (parentContainer.__featureIntroState ??= {
                            canContinue: false,
                            isExiting: false,
                            tapHandler: null,
                        }) as {
                            canContinue: boolean
                            isExiting: boolean
                            tapHandler: (() => void) | null
                        }

                        state.canContinue = false
                        state.isExiting = false

                        if (state.tapHandler) {
                            parentContainer.off('pointertap', state.tapHandler)
                        }

                        parentContainer.interactive = true
                        parentContainer.eventMode = 'static'
                        parentContainer.cursor = 'pointer'

                        state.tapHandler = () => {
                            if (!state.canContinue || state.isExiting) {
                                return
                            }

                            state.isExiting = true

                            // Advance FS/H&W gameplay immediately on dismiss — do not wait for
                            // you_won_out (~0.5s) or the first free spin / respin feels delayed.
                            ;(GameSignals as any).featurePopupDismissStarted?.emit()
                            ;(GameSignals as any).featureIntroSkipConfirmed?.emit()
                            ;(GameSignals as any).featureIntroContinue?.emit()

                            try {
                                animation.start(['you_won_out'], [false], 0, [1])
                            } catch (error) {
                                // Continue already advanced the round; outro is best-effort.
                            }
                        }

                        parentContainer.on('pointertap', state.tapHandler)

                        try {
                            animation.start(['you_won_in'], [false], 0, [1], () => {
                                if (state.isExiting) {
                                    return
                                }

                                animation.start(['you_won_loop'], [true], 0, [1])
                                state.canContinue = true
                            })
                            requestAnimationFrame(() => {
                                animation.visible = true
                            })
                        } catch (error) {
                            console.warn('[FeatureIntroPopup] Failed to play intro animation:', error)
                            ;(GameSignals as any).featureIntroContinue?.emit()
                        }
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                    callback: function (data: ICustomEventData) {
                        const animation: SpineAnimation = data.component.componentElement
                        const parentContainer: any =
                            data.component.parentContainer ||
                            (animation as any)?.parent ||
                            data.component.componentElement

                        if (!parentContainer) {
                            try {
                                animation.stop()
                            } catch (error) {}
                            animation.visible = false
                            return
                        }

                        const state = parentContainer.__featureIntroState as
                            | {
                                  canContinue: boolean
                                  isExiting: boolean
                                  tapHandler: (() => void) | null
                              }
                            | undefined

                        if (state?.tapHandler) {
                            parentContainer.off('pointertap', state.tapHandler)
                            state.tapHandler = null
                        }

                        parentContainer.interactive = false
                        parentContainer.eventMode = 'auto'
                        parentContainer.cursor = 'default'

                        try {
                            animation.stop()
                        } catch (error) {}

                        animation.visible = false
                    },
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.7,
                    y: 0.7,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0.7,
                    y: 0.7,
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 960,
                    y: 357,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 540,
                    y: 880,
                },
            ],
        },
        {
            name: 'Feature Intro Label',
            type: AssetType.TEXT,
            translationKey: '',
            anchor: 0.5,
            visible: false,
            textStyle: [
                {
                    orientation: Orientation.ANY,
                    options: {
                        fontFamily: 'MyriadPro-Black',
                        fontSize: 125,
                        fontWeight: '900',
                        fill: GRADIENT_GOLD_FILL,
                        stroke: { color: '#7A4E00', width: 2 },
                        dropShadow: {
                            color: '#3A2500',
                            blur: 4,
                            angle: Math.PI / 2,
                            distance: 4,
                            alpha: 1,
                        },
                        align: 'center',
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 590,
                    y: 440,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 162,
                    y: 960,
                },
            ],
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroDisplayRequested],
                    callback: function (data: ICustomEventData) {
                        const info = (data.signalData ?? {}) as {
                            kind?: 'freespins' | 'holdandwin'
                            freeSpinsCount?: number
                            retrigger?: boolean
                        }

                        featureIntroLabelReveal?.kill()

                        // Hold & Win shows the hold_win_logo sprite instead of this label.
                        if (!isFreeSpinsIntro(data)) {
                            data.component.componentElement.visible = false
                            return
                        }

                        featureIntroLabelReveal = gsap.delayedCall(FEATURE_INTRO_IN_ANIMATION_MS / 1000, () => {
                            const awardedCount = info.freeSpinsCount ?? 0
                            const labelKey = 'Free Spins'
                            const translatedLabel = TranslationUtils.translate(labelKey)
                            const label = translatedLabel === labelKey ? labelKey : translatedLabel
                            data.component.setText(info.retrigger ? `+${awardedCount} ${label}` : `${awardedCount} ${label}`)

                            data.component.componentElement.alpha = 1
                            data.component.componentElement.visible = true
                        })
                    },
                },
                {
                    event: ComponentEvent.HIDE,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featurePopupDismissStarted],
                    callback: function (data: ICustomEventData) {
                        featureIntroLabelReveal?.kill()
                        fadeOutFeaturePopupItem(data)
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                    callback: function () {
                        featureIntroLabelReveal?.kill()
                    },
                },
            ],
        },
        {
            name: 'Feature Intro Hold And Win Logo',
            type: AssetType.SPRITE,
            src: 'hold_win_logo.png',
            anchor: 0.5,
            visible: false,
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.7,
                    y: 0.7,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0.7,
                    y: 0.7,
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 597,
                    y: 440,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 177,
                    y: 960,
                },
            ],
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroDisplayRequested],
                    callback: function (data: ICustomEventData) {
                        featureIntroLogoReveal?.kill()

                        // Free Spins keeps the text label; this logo is Hold & Win only.
                        if (isFreeSpinsIntro(data)) {
                            data.component.componentElement.visible = false
                            return
                        }

                        featureIntroLogoReveal = gsap.delayedCall(FEATURE_INTRO_IN_ANIMATION_MS / 1000, () => {
                            data.component.componentElement.alpha = 1
                            data.component.componentElement.visible = true
                        })
                    },
                },
                {
                    event: ComponentEvent.HIDE,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featurePopupDismissStarted],
                    callback: function (data: ICustomEventData) {
                        featureIntroLogoReveal?.kill()
                        fadeOutFeaturePopupItem(data)
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                    callback: function () {
                        featureIntroLogoReveal?.kill()
                    },
                },
            ],
        },
        {
            name: 'Feature Intro Continue',
            type: AssetType.TEXT,
            translationKey: '',
            anchor: 0.5,
            visible: false,
            textStyle: [
                {
                    orientation: Orientation.ANY,
                    options: {
                        fontFamily: 'Montserrat_Bold',
                        fontSize: 50,
                        fill: 0xffffff,
                        align: 'center',
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 965,
                    y: 870,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 550,
                    y: 1420,
                },
            ],
            events: [
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroDisplayRequested],
                    callback: function (data: ICustomEventData) {
                        featureIntroContinueReveal?.kill()
                        featureIntroContinueReveal = gsap.delayedCall(FEATURE_INTRO_IN_ANIMATION_MS / 1000, () => {
                            const key = DeviceUtils.isMobileDevice() ? 'Tap to Continue' : 'Click to Continue'
                            data.component.setText(translate(key, key))

                            const el = data.component.componentElement as PIXI.Text
                            el.alpha = 1
                            el.visible = true
                            el.anchor.set(0.5)
                            gsap.killTweensOf(el.scale)
                            el.scale.set(1)
                            gsap.to(el.scale, {
                                x: 1.1,
                                y: 1.1,
                                duration: 0.6,
                                ease: 'sine.inOut',
                                yoyo: true,
                                repeat: -1,
                            })
                        })
                    },
                },
                {
                    event: ComponentEvent.HIDE,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featurePopupDismissStarted],
                    callback: function (data: ICustomEventData) {
                        featureIntroContinueReveal?.kill()
                        fadeOutFeaturePopupItem(data)
                    },
                },
                {
                    event: ComponentEvent.CUSTOM_NEW,
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                    callback: function (data: ICustomEventData) {
                        featureIntroContinueReveal?.kill()

                        const el = data.component.componentElement as PIXI.Text
                        gsap.killTweensOf(el.scale)
                        el.scale.set(1)
                    },
                },
            ],
        },
    ],
}
